import { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import CustomLoader from "shared/ui/loader";
import { checkQpayPayment, createQpayInvoice } from "../api/deliveryApi";
import { toast } from "react-toastify";

export default function QrCodeModal({
  show,
  onClose,
  orderId,
  deliveryPrice: amount,
  setDeliveryPrice,
}) {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [qrImage, setQrImage] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);

  // ✅ QR үүсгэх
  useEffect(() => {
    if (!show || !orderId || !amount) return;

    let isActive = true; // cleanup хамгаалалт
    setLoading(true);
    setTimeLeft(300);
    setQrImage(null);
    setInvoiceId(null);

    createQpayInvoice({ orderId, amount })
      .then(({ data }) => {
        if (!isActive) return;
        let response;
        try {
          response = typeof data === "string" ? JSON.parse(data) : data;
        } catch (err) {
          console.error("JSON parse error:", err);
          toast.error("Серверийн өгөгдөл буруу байна!");
          return;
        }

        if (response?.qr_image) {
          setQrImage(response.qr_image);
          setInvoiceId(response.invoice_id);
        } else {
          toast.error("QR код үүсгэж чадсангүй!");
        }
      })
      .catch((err) => {
        console.error("createQpayInvoice error:", err);
        toast.error("QR код үүсгэхэд алдаа гарлаа!");
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [show, orderId, amount]); // ✅ бүх dependency-гээ оруулсан

  // ✅ Countdown
  useEffect(() => {
    if (!loading && show && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [loading, show, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // ✅ Төлбөр шалгах
  const checkInvoice = async () => {
    if (!invoiceId) {
      toast.error("Invoice ID байхгүй байна!");
      return;
    }
    setChecking(true);
    try {
      // ⚠️ checkQpayPayment нь алдаа гарвал null буцаадаг (requests.js) — шууд
      //    задлавал TypeError өгч, төлбөр шалгах товч чимээгүй унана.
      const res = await checkQpayPayment({ invoice_id: invoiceId });
      if (res?.data?.count > 0) {
        toast.success("Төлбөр амжилттай төлөгдлөө!");
        handleClose();
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.info("Төлбөр хийгдээгүй байна. Түр хүлээгээд дахин шалгана уу.");
      }
    } catch (err) {
      console.error("Check payment error:", err);
      toast.error("Төлбөр шалгахад алдаа гарлаа.");
    } finally {
      setChecking(false);
    }
  };

  // ✅ Modal хаах (setDeliveryPrice null болгохыг зөв цагт)
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setDeliveryPrice?.(null);
      setQrImage(null);
      setInvoiceId(null);
    }, 300);
  };

  // ✅ АВТОМАТ шалгалт — QR харагдаж байх зуур 5с тутам (хэрэглэгч гараар дарах
  //    шаардлагагүй). Төлбөр олдвол хаана. Алдааг чимээгүй алгасч дараагийн
  //    давталтаар дахин оролдоно (гараар шалгалт дээр алдааг харуулна).
  useEffect(() => {
    if (loading || !show || !invoiceId) return undefined;
    let active = true;
    const iv = setInterval(async () => {
      try {
        const res = await checkQpayPayment({ invoice_id: invoiceId });
        if (active && res?.data?.count > 0) {
          clearInterval(iv);
          toast.success("Төлбөр амжилттай төлөгдлөө!");
          handleClose();
          setTimeout(() => window.location.reload(), 1000);
        }
      } catch (e) {
        /* сүлжээ/сервер — дараагийн 5с-д дахин оролдоно */
      }
    }, 5000);
    return () => {
      active = false;
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, show, invoiceId]);

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="bg-danger text-white">
        <Modal.Title>Төлбөр төлөх</Modal.Title>
      </Modal.Header>

      <Modal.Body className="text-center">
        {loading ? (
          <CustomLoader fontSize={32} showTitle />
        ) : qrImage ? (
          <>
            <img
              src={`data:image/png;base64,${qrImage}`}
              alt="QR код"
              style={{ maxWidth: 250 }}
              className="mb-3"
            />
            <p className="text-muted mb-2 small">
              {timeLeft > 0 ? formatTime(timeLeft) : "Хугацаа дууссан"}
            </p>
            <p className="text-muted">
              QR код уншуулж төлбөрөө төлнө үү
              <br />
              <small>(Төлбөрийг автоматаар шалгаж байна…)</small>
            </p>
          </>
        ) : (
          <p className="text-danger">QR код үүсгэхэд алдаа гарлаа</p>
        )}
      </Modal.Body>

      {!loading && qrImage && (
        <Modal.Footer>
          <button
            className="btn btn-danger d-flex align-items-center justify-content-center"
            onClick={checkInvoice}
            disabled={checking}
          >
            {checking ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Түр хүлээнэ үү...
              </>
            ) : (
              "Төлбөр шалгах"
            )}
          </button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
